import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMyUser } from "../../../features/user/hooks/useMyUser";
import { usePatchUser } from "../../../features/user/hooks/usePatchUser";
import { usePasswordStrength } from "../../../common/hooks/usePasswordStrength";
import authAPI from "../../../api/authAPI";
import Button from "../../_shared/Button/Button";
import Input from "../../_shared/Input/Input";
import Badge from "../../_shared/Badge/Badge";
import { ChevronDown, IconCheckMark } from "../../../common/Icons/Icons";
import PasswordStrengthIndicator from "../../_shared/PasswordStrengthIndicator/PasswordStrengthIndicator";
import styles from "../Settings.module.css";

const UserSettings = () => {
  const { t, i18n } = useTranslation("common");
  const myUser = useMyUser();
  const { mutate: patchUser } = usePatchUser();
  const [showSetPassword, setShowSetPassword] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const user = myUser?.data;
  const hasPassword = user?.password !== null && user?.password !== undefined;
  const hasFeide = user?.feideId !== null && user?.feideId !== undefined;
  const { strength: passwordStrength } = usePasswordStrength(password);

  const languages = {
    nb_NO: "Bokmål",
    nn_NO: "Nynorsk",
  };

  function changeWebsiteLanguage(lang) {
    i18n.changeLanguage(lang);
    patchUser({ language: lang });
    setShowLanguageDropdown(false);
  }

  async function handleSetPassword(e) {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (password !== confirmPassword) {
      setError("Passordene matcher ikke.");
      return;
    }

    if (password.length < 8) {
      setError("Passordet må være minst 8 tegn langt.");
      return;
    }

    setIsSubmitting(true);

    try {
      await authAPI.setPassword(password);
      setSuccess(t("auth.settings.passwordSet"));
      setPassword("");
      setConfirmPassword("");
      setShowSetPassword(false);
      myUser.refetch();
    } catch (err) {
      setError(
        err.response?.data?.message || t("auth.settings.passwordSetError")
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>
          {t("auth.settings.authMethods")}
        </h2>

        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabelHard}>
              {t("auth.settings.feide")}
            </span>
            <div className={styles.authMethodStatus}>
              {hasFeide ? (
                <>
                  {/*<Badge variant="success">*/}
                  <span className={styles.infoValue}>
                    <IconCheckMark style={{ maxWidth: 24, maxHeight: 24 }} />{" "}
                    {t("auth.settings.connected")}
                  </span>
                  {/*</Badge>*/}
                  {/*{user?.feideId && (*/}
                  {/*  <span className={styles.authMethodDetail}>*/}
                  {/*    {t("auth.settings.feideId")}: {user.feideId}*/}
                  {/*  </span>*/}
                  {/*)}*/}
                </>
              ) : (
                <>
                  <Badge variant="neutral">
                    {t("auth.settings.notConnected")}
                  </Badge>
                  <Button variant="primary" size="sm" disabled>
                    {t("auth.settings.linkFeide")}
                  </Button>
                </>
              )}
            </div>
          </div>

          <div className={styles.infoItem}>
            <span className={styles.infoLabelHard}>
              {t("auth.settings.password")}
            </span>
            <div className={styles.authMethodStatus}>
              {!hasPassword && !showSetPassword && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setShowSetPassword(true)}
                >
                  {t("auth.settings.setPassword")}
                </Button>
              )}

              {!hasPassword && showSetPassword && (
                <form
                  className={styles.passwordForm}
                  onSubmit={handleSetPassword}
                >
                  <Input
                    label={t("auth.resetPassword.newPassword")}
                    type="password"
                    value={password}
                    onChange={setPassword}
                    required
                    disabled={isSubmitting}
                    minLength={8}
                  />
                  <PasswordStrengthIndicator password={password} />

                  <Input
                    label={t("auth.resetPassword.confirmPassword")}
                    type="password"
                    value={confirmPassword}
                    onChange={setConfirmPassword}
                    required
                    disabled={isSubmitting}
                    minLength={8}
                  />

                  <div className={styles.formActions}>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={isSubmitting || !password || !confirmPassword}
                    >
                      {isSubmitting
                        ? t("auth.settings.saving")
                        : t("auth.settings.savePassword")}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setShowSetPassword(false);
                        setPassword("");
                        setConfirmPassword("");
                        setError(null);
                      }}
                      disabled={isSubmitting}
                    >
                      {t("cancel")}
                    </Button>
                  </div>
                </form>
              )}

              {hasPassword && (
                <span className={styles.infoValue}>
                  {t("auth.settings.passwordEnabled")}
                </span>
              )}
            </div>
          </div>
        </div>

        {error && <div className={styles.error}>{error}</div>}
        {success && <div className={styles.success}>{success}</div>}
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>
          {t("auth.settings.accountInfo")}
        </h2>
        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>{t("auth.settings.name")}</span>
            <span className={styles.infoValue}>{user?.fullName || "—"}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>{t("auth.email")}</span>
            <span className={styles.infoValue}>{user?.email || "—"}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>
              {t("auth.settings.language")}
            </span>
            <div className={styles.languageSelect}>
              <button
                onClick={() => setShowLanguageDropdown((prev) => !prev)}
                className={styles.languageButton}
              >
                {languages[i18n.language] || languages["nb_NO"]}
                <ChevronDown className={styles.chevron} />
              </button>
              {showLanguageDropdown && (
                <div className={styles.languageDropdown}>
                  <button
                    onClick={() => changeWebsiteLanguage("nb_NO")}
                    className={
                      i18n.language === "nb_NO" ? styles.activeLanguage : ""
                    }
                  >
                    Bokmål
                  </button>
                  <button
                    onClick={() => changeWebsiteLanguage("nn_NO")}
                    className={
                      i18n.language === "nn_NO" ? styles.activeLanguage : ""
                    }
                  >
                    Nynorsk
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default UserSettings;
